import { useState } from 'react';
import { IapScope } from '../../ICSConstants';
import {
  useCreateIncidentObjective,
  useIncidentObjectives,
} from '../../hooks/incidentObjectiveHooks';
import { useIncidentStrategies } from '../../hooks/incidentStrategyHooks';
import { useIncidentTactics } from '../../hooks/incidentTacticHooks';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { Badge } from '@/components/ui/badge';
import { OBJECTIVES_LIBRARY } from './ostLibraryData';
import ObjectiveRows from './WorkAnalysisTable/ObjectiveRows';
import AddChildRow from './WorkAnalysisTable/AddChildRow';

type WorkAnalysisTableProps = {
  readOnly?: boolean;
  hideAddButton?: boolean;
  searchTerm?: string;
  adding?: boolean;
  onAddingChange?: (adding: boolean) => void;
};

const WorkAnalysisTable: React.FC<WorkAnalysisTableProps> = ({
  readOnly = false,
  hideAddButton = false,
  searchTerm = '',
  adding: addingProp,
  onAddingChange,
}) => {
  const scope = 'incident' as IapScope;
  const { data: objectives } = useIncidentObjectives(scope);
  const { data: strategies } = useIncidentStrategies(scope);
  const { data: tactics } = useIncidentTactics(scope);
  const createObjective = useCreateIncidentObjective();
  const [internalAdding, setInternalAdding] = useState(false);
  const adding = hideAddButton ? (addingProp ?? false) : internalAdding;
  const setAdding = (v: boolean) => {
    if (hideAddButton) {
      onAddingChange?.(v);
    } else {
      setInternalAdding(v);
    }
  };

  const filteredObjectives =
    objectives?.filter((obj) => {
      if (obj.archived) return false;
      const type = obj.type || 'Operational';
      const isOperationalType =
        type === 'Operational' || type === 'OperationalAndManagerial';
      return (
        isOperationalType &&
        obj.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }) ?? [];

  const handleCreateObjective = async (name: string) => {
    await createObjective.mutateAsync({
      name,
      description: '',
      type: 'Operational',
    });
    setAdding(false);
  };

  return (
    <div className="w-100">
      {!readOnly && !hideAddButton && (
        <div className="d-flex justify-content-end mb-2">
          <StylishNewButton onClick={() => setAdding(!adding)}>
            <SharedIcon iconName="add" classes="me-1" size="sm" />
            Add Objective
          </StylishNewButton>
        </div>
      )}
      <table className="v-top mb-0 table">
        <thead>
          <tr>
            <th style={{ width: '30%' }}>
              4. Objectives
              <div>Desired Outcome</div>
            </th>
            <th style={{ width: '35%' }}>
              5. Strategies
              <div>How</div>
            </th>
            <th style={{ width: '35%' }}>
              6. Tactics
              <div>Who / What / Where / When</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {adding && !readOnly && (
            <tr>
              <td style={{ verticalAlign: 'top' }}>
                <div className="d-flex flex-column gap-2">
                  <AddChildRow
                    options={OBJECTIVES_LIBRARY}
                    placeholder="Select or create Objective..."
                    customInputMaxLength={200}
                    onCreate={(label) => handleCreateObjective(label)}
                    onCancel={() => setAdding(false)}
                  />
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <Badge variant="outline" className="tw:text-white">
                      Objectives
                    </Badge>
                    <Badge variant="outline" className="tw:text-white">
                      Operational
                    </Badge>
                  </div>
                </div>
              </td>
              <td style={{ verticalAlign: 'top' }} />
              <td style={{ verticalAlign: 'top' }} />
            </tr>
          )}
          {filteredObjectives.map((obj) => (
            <ObjectiveRows
              key={obj.objective_id}
              objective={obj}
              strategies={strategies ?? []}
              tactics={tactics ?? []}
              readOnly={readOnly}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WorkAnalysisTable;
